import { useState, useEffect, useMemo, useRef } from "react";

interface TypewriterItem {
  content: string;
}

export interface UseTypewriterProps<T extends TypewriterItem> {
  list: T[];
  speed: number;
  enabled: boolean;
}

export const useTypewriter = <T extends TypewriterItem>({
  list = [],
  speed = 20,
  enabled = true,
}: UseTypewriterProps<T>) => {
  // 索引初始化，确保初始值合法
  const [currentIndex, setCurrentIndex] = useState(() => Math.max(0, list.length - 1));
  const [displayedText, setDisplayedText] = useState("");

  // 核心计时器优化
  useEffect(() => {
    // 如果不启用，或者已经打到了物理限制，直接退出
    if (!enabled || list.length === 0 || currentIndex >= list.length) return;

    const targetText = list[currentIndex].content || "";

    if (displayedText.length < targetText.length) {
      // 逐字增加
      const timeout = setTimeout(() => {
        setDisplayedText(targetText.slice(0, displayedText.length + 1));
      }, speed);
      return () => clearTimeout(timeout);
    } else if (currentIndex < list.length - 1) {
      // 当前项完成，等待一小段时间切换到下一项
      const nextTimeout = setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        setDisplayedText("");
      }, speed * 2);
      return () => clearTimeout(nextTimeout);
    }
  }, [currentIndex, displayedText.length, enabled, speed, list.length]);

  // 渲染列表
  const visibleList = useMemo(() => {
    if (list.length === 0) return [];

    return list.slice(0, currentIndex + 1).map((item, index) => {
      const isCurrent = index === currentIndex;
      const isComplete = index < currentIndex || displayedText === item.content;

      return {
        ...item,
        content: isCurrent ? displayedText : item.content,
        isComplete,
      };
    });
  }, [list, currentIndex, displayedText]);

  return {
    visibleList,
    isFinished: currentIndex === list.length - 1 && displayedText === (list[list.length - 1]?.content || "")
  };
};